import React, { useEffect, useState } from "react";

import SyDialog from "../../../components/SyDialog";
import { showErrorToast, showSuccessToast } from "../../../components/Toast";
import useAllMyReviews from "../useAllMyReviews";
import useReviewTarget from "../useReviewTarget";
import { useSubmitReview } from "../useReviewActions";
import TargetIdentity from "./TargetIdentity";
import { StarInput } from "./Stars";
import { REVIEWS_COPY, serverMessage } from "../reviewsCopy";
import { asTargetId, findReviewFor, normalizeTargetType } from "../reviewsMappers";
import { MIN_RATING, REVIEW_MAX_LENGTH } from "../reviewsService";

import "../styles.css";

const ReviewFormDialog = ({ review, targetType, targetId, targetName, onClose, onSaved }) => {
  const type = normalizeTargetType(review?.targetType || targetType);
  const id = asTargetId(review?.targetId ?? targetId);

  const target = useReviewTarget({
    targetType: type,
    targetId: id,
    fallbackName: targetName,
  });

  const mine = useAllMyReviews({ enabled: !review && Boolean(id) });

  const [existing, setExisting] = useState(review || null);
  const [rating, setRating] = useState(review ? Math.round(review.rating) || 0 : 0);
  const [text, setText] = useState(review?.review || "");
  const [ready, setReady] = useState(Boolean(review) || !id);

  const { submit, submitting } = useSubmitReview();

  useEffect(() => {
    if (ready || mine.isLoading) return;

    const found = findReviewFor(mine.items, { targetType: type, targetId: id });

    if (found) {
      setExisting(found);
      setRating(Math.round(found.rating) || 0);
      setText(found.review || "");
    }

    setReady(true);
  }, [ready, mine.isLoading, mine.items, type, id]);

  const canSubmit = Boolean(id) && rating >= MIN_RATING && !submitting;

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    const result = await submit({
      targetType: type,
      targetId: id,
      rating,
      review: text,
      existingId: existing?.id,
    });

    if (result.status === "error") {
      showErrorToast(serverMessage(result.error, REVIEWS_COPY.submitFailed));

      return;
    }

    const copy = result.status === "updated" ? REVIEWS_COPY.updated : REVIEWS_COPY.created;

    showSuccessToast(copy.message, copy.title);
    onSaved?.(result);
    onClose();
  };

  const name = target.name || targetName || target.subtitle;

  return (
    <SyDialog
      open
      size="lg"
      className="rv-dialog"
      title={existing ? "Edit Review" : "Write A Review"}
      subtitle={name}
      onClose={onClose}
    >
      <form className="rv-form" onSubmit={onSubmit}>
        <header className="rv-form-target">
          <TargetIdentity name={name} imageUrl={target.imageUrl} size={44} />

          <div>
            <p className="rv-form-name">{name}</p>
            <p className="rv-card-sub">{target.subtitle}</p>
          </div>
        </header>

        <h3 className="rv-form-question">How was your experience?</h3>

        <StarInput value={rating} onChange={setRating} />

        <label className="rv-form-label" htmlFor="rv-review">
          Your review <span>(optional)</span>
        </label>

        <textarea
          id="rv-review"
          className="rv-form-text"
          placeholder="Share details about your experience…"
          maxLength={REVIEW_MAX_LENGTH}
          value={text}
          disabled={!ready}
          onChange={(event) => setText(event.target.value)}
        />

        <p className="rv-form-counter">
          {text.length} / {REVIEW_MAX_LENGTH}
        </p>

        <div className="rv-form-actions">
          <button type="button" className="rv-btn rv-btn-outline" onClick={onClose}>
            Cancel
          </button>

          <button
            type="submit"
            className="rv-btn rv-btn-primary"
            disabled={!canSubmit || !ready}
          >
            {submitting ? "Saving…" : existing ? "Update Review" : "Submit Review"}
          </button>
        </div>
      </form>
    </SyDialog>
  );
};

export default ReviewFormDialog;
